import ElementReversibleAction from '../models/ElementReversibleAction'
import { useElementHistoryStore } from './ElementHistoryStore'

/**
 * Records an undoable change whose writes go through the save coordinator, without holding the undo queue for the
 * network (S.0 "Waiting for writes"). `ElementHistoryStore` runs every undoable action of the app one at a time, and
 * a write can wait behind an earlier retrying save of its element for a minute; so the history action only starts
 * the write (`forward()` / `backward()` must queue it and make their local change synchronously, before their first
 * `await`), and the caller waits for the save here, outside that queue.
 *
 * Resolves with the save's result; rejects when it failed, after removing the change from the undo stack (undo must
 * not replay a change the server never made). Undo and redo do not wait: their failures are reported by the save
 * coordinator.
 */
export async function doQueuedAction<T>(
  rootEntityId: string,
  name: string,
  forward: (replay: boolean) => Promise<T>,
  backward: () => Promise<unknown>
): Promise<T> {
  const historyStore = useElementHistoryStore()
  let saved: Promise<T> | undefined
  const action = new ElementReversibleAction<void, void>()
  action.name = name
  let runs = 0
  action.action = async () => {
    // `replay`: a redo, where the caller is no longer there to show its own error
    saved = forward(runs++ > 0)
    saved.catch(() => {})
  }
  action.reverseAction = async () => {
    backward().catch(() => {})
  }
  await historyStore.doAction(rootEntityId, action)
  try {
    return await saved!
  } catch (error) {
    historyStore.discardAction(rootEntityId, action)
    throw error
  }
}
